import { Button, Typography } from '@mui/material'
import { useState } from 'react'
import { IconButton, InformationModal } from 'utoniq-ui'

import { BaseLayout, Head } from '@/components/layouts'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { PasswordResetForm, PasswordResetFormProps } from './PasswordResetForm'

export * from './fragments'

export type State = {
  email: string
}

export type Action = {
  onSubmitPasswordReset: PasswordResetFormProps['onSubmitPasswordReset']
}

export const PasswordResetTemplate: Template<State, Action> = ({
  state,
  onSubmitPasswordReset,
}) => {
  const { t } = useTranslation('passwordReset')
  const { showError } = useMessage()
  const { router, routerPages, routerNames } = useRouter()

  const [open, setOpen] = useState(false)

  const onReset: PasswordResetFormProps['onSubmitPasswordReset'] = values => {
    return onSubmitPasswordReset(values)
      .then(() => {
        setOpen(true)
      })
      .catch(e => {
        showError(e)
      })
  }

  return (
    <BaseLayout
      margin
      header={{
        title: t('title'),
        left: (
          <IconButton.Back
            edge="start"
            aria-label="menu"
            onClick={() => router.push(routerPages.Top)}
          />
        ),
      }}
    >
      <Head title={routerNames.PasswordReset} />
      <TextCell email={state.email} />
      <PasswordResetForm
        onSubmitPasswordReset={onReset}
        onOpenModal={() => setOpen(true)}
      />
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('completeModalTitle')}
        content={t('completeModalDescription')}
        buttons={
          <Button
            onClick={() => setOpen(false)}
            sx={{ width: 212 }}
            size="large"
          >
            OK
          </Button>
        }
      />
    </BaseLayout>
  )
}

const TextCell: Cell<{ email: string | null }> = ({ email }) => {
  const { t } = useTranslation('passwordReset')

  return (
    <CellFrame sx={{ pb: 6 }}>
      <Typography variant="subtitle1" sx={{ py: 2 }}>
        {t('email')}
      </Typography>
      <Typography variant="body2">{email}</Typography>
    </CellFrame>
  )
}
